import { cn } from "@/lib/utils";
import type { NormalizedEvent } from "@/services/types/agent-gateway";
import { SafeMarkdown } from "./SafeMarkdown";
import {
  formatEventAuthor,
  formatEventTime,
  isUserAuthor,
} from "./event-utils";

type MessageEvent = Extract<NormalizedEvent, { type: "message" }>;
type AgentMessageProps = {
  event: MessageEvent;
};

const avatarColours = [
  "bg-[#102D03]",
  "bg-[#2A2F48]",
  "bg-[#393420]",
  "bg-[#240C2E]",
];

function authorInitials(author: string) {
  return formatEventAuthor(author)
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0])
    .join("")
    .toUpperCase();
}

function avatarColour(author: string) {
  const index = [...author].reduce(
    (total, character) => total + character.charCodeAt(0),
    0,
  );
  return avatarColours[index % avatarColours.length];
}

export function AgentMessage({ event }: AgentMessageProps) {
  const userMessage = isUserAuthor(event.author);
  const author = formatEventAuthor(event.author);

  if (userMessage) {
    return (
      <article
        aria-label={`Message from ${author}`}
        className="flex min-w-0 justify-end"
      >
        <div className="min-w-0 max-w-[88%] rounded-2xl rounded-br-md bg-brand-darkGreen px-4 py-3 text-sm text-white shadow-xs md:max-w-[75%]">
          <div className="mb-2 flex min-w-0 flex-wrap items-center justify-between gap-x-4 gap-y-1 text-xs opacity-70">
            <span className="font-medium [overflow-wrap:anywhere]">
              {author}
            </span>
            <time className="shrink-0" dateTime={event.created_at ?? undefined}>
              {formatEventTime(event.created_at)}
            </time>
          </div>
          <p className="whitespace-pre-wrap [overflow-wrap:anywhere]">
            {event.content.text}
          </p>
        </div>
      </article>
    );
  }

  return (
    <article
      aria-label={`Message from ${author}`}
      className="flex min-w-0 items-start gap-3"
    >
      <span
        aria-hidden="true"
        className={cn(
          "flex size-8 shrink-0 items-center justify-center rounded-full font-medium text-white text-xs",
          avatarColour(event.author),
        )}
      >
        {authorInitials(event.author)}
      </span>
      <div className="min-w-0 flex-1">
        <div className="mb-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
          <span className="font-medium text-[#212529]">{author}</span>
          <time
            className="text-[#6C757D]"
            dateTime={event.created_at ?? undefined}
          >
            {formatEventTime(event.created_at)}
          </time>
        </div>
        <div className="rounded-tl-sm rounded-tr-xl rounded-br-xl rounded-bl-xl bg-[#F8F9FA] px-4 py-3 text-[#343A40] text-sm">
          <SafeMarkdown>{event.content.text}</SafeMarkdown>
        </div>
      </div>
    </article>
  );
}
